import { baseAction } from "./IActionBase";

type ExtractPayloadType<T> = T extends Array<infer U> ? U[] : T;

/**
 * Action creator signature: nếu payload type chứa `undefined` (vd `undefined as undefined`),
 * payload optional → cho phép gọi `LOAD_START()` không args. Ngược lại payload required.
 *
 * Pragmatic deviation từ ESS_TKB_Web (ESS yêu cầu truyền `undefined` explicit) — giảm noise
 * tại consumer side cho action không có payload.
 */
type ActionCreator<P> = undefined extends P ? (payload?: P) => any : (payload: P) => any;

/**
 * Generate full set of action creators từ một action type definition object.
 *
 * @example
 * const eAuthActionTypeDefine = { LOGIN_START: {} as ILoginRequest, LOGIN_SUCCESS: {} as ILoginRespone };
 * const authActions = generateActions(eAuthActionTypeDefine, "AUTH");
 * // authActions.LOGIN_START(req) → { type: "AUTH_LOGIN_START", payload: req }
 */
export function generateActions<T extends Record<string, any>>(
    actionTypes: T,
    prefix: string
): { [K in keyof T]: ActionCreator<ExtractPayloadType<T[K]>> } {
    const actions: any = {};
    (Object.keys(actionTypes) as Array<keyof T>).forEach((key: any) => {
        actions[key] = (payload?: ExtractPayloadType<T[typeof key]>) =>
            baseAction(`${prefix}_${key}`, payload);
    });
    return actions;
}

/**
 * Generate 1 action creator có chạy side-effect trước khi return action.
 * Dùng để override action mặc định từ generateActions, ví dụ inject saveAccessToken
 * khi LOGIN_SUCCESS dispatched.
 */
export function generateActionForKey<T extends Record<string, any>, K extends keyof T>(
    actionTypes: T,
    prefix: string,
    key: K,
    preProcessFunc?: (payload: ExtractPayloadType<T[K]>) => any
): { [P in K]: ActionCreator<ExtractPayloadType<T[P]>> } {
    const action = {
        [key]: (payload?: ExtractPayloadType<T[typeof key]>) => {
            if (preProcessFunc) preProcessFunc(payload as ExtractPayloadType<T[K]>);
            return baseAction(`${prefix}_${key.toString()}`, payload);
        },
    } as unknown as { [P in K]: ActionCreator<ExtractPayloadType<T[P]>> };

    return action;
}
